/**
 * PersonaPicker — Studio tab sidebar component.
 * Select dropdown: "None", persona list, "Create New...".
 * When active: compact card showing icon, name, genre, source badge.
 */

import React, { useState } from 'react';
import { Users, Plus, ChevronDown, ChevronUp, X, Pencil } from 'lucide-react';
import { PersonaCard } from './PersonaCard';
import type { Persona } from '@/types/persona.types';

interface PersonaPickerProps {
  personas: Persona[];
  activePersonaId: string | null;
  onSelect: (id: string | null) => void;
  onCreateNew: () => void;
  onDelete: (id: string) => void;
  onEdit?: (persona: Persona) => void;
}

export const PersonaPicker: React.FC<PersonaPickerProps> = ({
  personas,
  activePersonaId,
  onSelect,
  onCreateNew,
  onDelete,
  onEdit,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const activePersona = personas.find(p => p.id === activePersonaId) || null;

  return (
    <div className={`rounded-xl border transition-all ${
      activePersona ? 'border-amber-500/30 bg-amber-500/5' : 'border-slate-800 bg-slate-900/30'
    }`}>
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center gap-3 p-3 text-left"
      >
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
          activePersona ? 'bg-amber-500/20 text-amber-400' : 'bg-slate-800 text-slate-500'
        }`}>
          <Users size={16} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[11px] font-black text-slate-400 uppercase tracking-widest">Persona</p>
          <p className="text-sm font-bold text-slate-200 truncate">
            {activePersona ? activePersona.name : 'None'}
          </p>
        </div>
        {isExpanded ? <ChevronUp size={16} className="text-slate-500" /> : <ChevronDown size={16} className="text-slate-500" />}
      </button>

      {/* Active persona compact card */}
      {activePersona && !isExpanded && (
        <div className="px-3 pb-3">
          <PersonaCard persona={activePersona} isActive compact />
          <div className="mt-2 flex items-center gap-2">
            {onEdit && (
              <button
                onClick={() => onEdit(activePersona)}
                className="flex-1 flex items-center justify-center gap-1.5 py-1.5 text-[10px] font-bold uppercase tracking-widest text-amber-400 hover:text-amber-300 transition-colors"
              >
                <Pencil size={12} /> Edit
              </button>
            )}
            <button
              onClick={() => onSelect(null)}
              className="flex-1 flex items-center justify-center gap-1.5 py-1.5 text-[10px] font-bold uppercase tracking-widest text-slate-500 hover:text-red-400 transition-colors"
            >
              <X size={12} /> Deactivate
            </button>
          </div>
        </div>
      )}

      {/* Expanded: full persona list */}
      {isExpanded && (
        <div className="px-3 pb-3 space-y-2 border-t border-slate-800 pt-3">
          {/* None option */}
          <button
            onClick={() => { onSelect(null); setIsExpanded(false); }}
            className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
              !activePersonaId ? 'bg-slate-800 text-slate-200 font-bold' : 'text-slate-400 hover:bg-slate-800/50'
            }`}
          >
            None (manual config)
          </button>

          {/* Persona list */}
          {personas.map(p => (
            <button
              key={p.id}
              onClick={() => { onSelect(p.id); setIsExpanded(false); }}
              className="w-full text-left"
            >
              <PersonaCard
                persona={p}
                isActive={p.id === activePersonaId}
                compact
                onDelete={onDelete}
              />
            </button>
          ))}

          {/* Create new */}
          <button
            onClick={() => { onCreateNew(); setIsExpanded(false); }}
            className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-bold text-amber-400 hover:bg-amber-500/10 border border-dashed border-amber-500/30 transition-colors"
          >
            <Plus size={16} /> Create New Persona...
          </button>
        </div>
      )}
    </div>
  );
};
